import { cn } from "@/v2/lib/utils";
import { Label } from "@/v2/components/ui/label";
import { Badge } from "@/v2/components/ui/badge";
import { AppIcon } from "@/v2/components/EntryCard";
import { formatTime } from "@/shared/utils/formatTime";
import { useMattersCache } from "@/shared/cache";
import type { Entry } from "@/shared/types/entries";

export interface MainSourcePickerProps {
	entries: Entry[];
	value: number;
	onChange: (id: number) => void;
	className?: string;
}

/**
 * Radio-style list of source entries used by the Collapse dialog to pick the
 * "main entry" that anchors the new narrative. Styled to mirror the v2 mobile
 * EntryCard so the picker reads as "these are your entries" rather than as a
 * generic radio list. Selection is indicated by a `bg-primary/10` tint and a
 * left rail.
 */
export function MainSourcePicker({
	entries,
	value,
	onChange,
	className,
}: MainSourcePickerProps) {
	const { matters } = useMattersCache();

	return (
		<div className={cn("grid w-full gap-1.5", className)}>
			<Label>Main entry</Label>
			<p className="text-[11px] text-muted-foreground">
				The narrative is built around this entry; other selected entries become supporting context.
			</p>
			<div className="grid divide-y divide-border overflow-hidden rounded-md border border-border bg-card">
				{entries.map((e) => {
					const m = matters.find((mat) => mat.matter_id === e.matter_id);
					const matterDisplay = e.matter || (m ? m.matter.trim() : "");
					const matterName = e.matter_name ?? m?.matter ?? null;
					const client = e.client ?? m?.client ?? null;
					const isMain = e.id === value;
					return (
						<button
							key={e.id}
							type="button"
							onClick={() => onChange(e.id)}
							className={cn(
								"relative w-full px-3 py-2.5 text-left transition-colors",
								isMain
									? "bg-primary/10 border-l-2 border-l-primary"
									: "hover:bg-secondary/40 active:bg-secondary/60 border-l-2 border-l-transparent",
							)}
						>
							<div className="flex items-start gap-2.5">
								<div className="shrink-0 mt-0.5 w-7 h-7 flex items-center justify-center [&_svg]:size-7 [&_img]:size-7">
									<AppIcon application={e.application} />
								</div>
								<div className="flex-1 min-w-0">
									<div className="flex items-start justify-between gap-2">
										<div className="min-w-0 -my-0.5">
											<p className="truncate text-xs font-medium leading-tight">
												{matterDisplay || <span className="text-muted-foreground italic">No matter</span>}
											</p>
											{(matterName || client) && (
												<p className="truncate text-[11px] text-muted-foreground leading-tight">
													{[matterName, client].filter(Boolean).join(" · ")}
												</p>
											)}
											{e.end_time && (
												<p className="text-[10px] text-muted-foreground/70 leading-tight">
													{formatTime(e.end_time)}
												</p>
											)}
										</div>
										<span className="text-[11px] font-medium text-foreground/70 shrink-0 tabular-nums">
											{e.duration.toFixed(2)}h
										</span>
									</div>
									{e.description && (
										<p className="mt-1.5 line-clamp-3 text-xs text-muted-foreground whitespace-pre-wrap">
											{e.description}
										</p>
									)}
									{(e.task || e.activity) && (
										<div className="flex items-center gap-1.5 mt-1.5">
											{e.task && <Badge variant="muted">{e.task}</Badge>}
											{e.activity && <Badge variant="muted">{e.activity}</Badge>}
										</div>
									)}
								</div>
							</div>
						</button>
					);
				})}
			</div>
		</div>
	);
}
